/* Panneau des processus — la popin de la page /pages/processes.
 *
 * Une seule fenêtre de dialogue pour toutes les cartes de la grille : à
 * l'ouverture, on y recopie le titre et le contenu de la carte cliquée, tirés
 * de ses deux <template> (rendus par Liquid, donc inertes tant qu'on ne les
 * clone pas : ni la vidéo ni la rangée de produits ne se chargent avant).
 *
 * Le <dialog> modal natif fait le reste, comme celui du panier : Échap, focus
 * retenu dedans puis rendu à la carte à la fermeture, page derrière inerte.
 * Un clic sur le voile (hors du panneau) ferme aussi.
 *
 * À la fermeture, le contenu est vidé : la vidéo s'arrête et la rangée de
 * produits sera redemandée à la prochaine ouverture.
 *
 * Fichier dans `assets/` plutôt qu'un bloc {% javascript %} : `theme dev`
 * tronque le bundle `scripts.js` au-delà d'un seuil variable (voir CLAUDE.md).
 *
 * Contrat de markup :
 *
 *   Dans la page, une carte par processus :
 *     [data-process-open]      bouton qui ouvre le panneau de sa carte
 *     template[data-process-title]    le titre (texte)
 *     template[data-process-content]  le contenu du panneau
 *     (les trois dans un même parent, la carte)
 *
 *   <process-panel> contient :
 *     <dialog>
 *       [data-process-panel-title]  reçoit le titre
 *       [data-process-panel-body]   reçoit le contenu
 *       [data-process-close]        bouton de fermeture
 */

class ProcessPanel extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    /* Pas `this.title` : c'est la réflexion native de l'attribut title,
       l'élément y deviendrait une chaîne (même piège que `slot`). */
    this.heading = this.querySelector('[data-process-panel-title]');
    this.body = this.querySelector('[data-process-panel-body]');

    /* Délégué sur le document : les cartes sont ailleurs dans la section. */
    this.onClick = (event) => {
      const button = event.target.closest('[data-process-open]');
      if (button) this.open(button.parentElement);
    };
    document.addEventListener('click', this.onClick);

    /* La rangée de produits lit tout le catalogue (voir
       `product-material-products.js`), ce qui prend une à deux secondes :
       on commence dès que le pointeur ou le focus arrive sur une carte qui
       en a une, pour que tout soit souvent là au clic. */
    this.onIntent = (event) => {
      const button = event.target.closest && event.target.closest('[data-process-open]');
      if (button) this.prefetch(button.parentElement);
    };
    document.addEventListener('pointerover', this.onIntent, { passive: true });
    document.addEventListener('focusin', this.onIntent);

    /* Et, sans attendre personne, dès que la page est au repos, si au moins
       une carte a des produits : l'index est alors presque toujours prêt au
       premier clic. Une seule page du site s'en sert, c'est un coût accepté
       (cinq requêtes d'environ 100 Ko de HTML). */
    const first = [...document.querySelectorAll('template[data-process-content]')].find((template) =>
      template.content.querySelector('product-material-products[data-index-url]')
    );
    if (first) {
      const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1500));
      idle(() => this.prefetch(first.parentElement));
    }

    this.querySelector('[data-process-close]').addEventListener('click', () => this.dialog.close());

    /* Le dialogue est le panneau lui-même : un clic dont la cible est le
       <dialog> et qui tombe hors de son rectangle vient du voile. */
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) this.dialog.close();
    });

    this.dialog.addEventListener('close', () => {
      this.body.replaceChildren();
      this.heading.textContent = '';
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('pointerover', this.onIntent);
    document.removeEventListener('focusin', this.onIntent);
  }

  prefetch(card) {
    const content = card.querySelector('template[data-process-content]');
    const row = content && content.content.querySelector('product-material-products[data-index-url]');
    if (!row) return;

    /* Le composant de la rangée vient d'un autre fichier, chargé après
       celui-ci : on attend qu'il soit défini plutôt que d'abandonner. */
    customElements.whenDefined('product-material-products').then((Row) => {
      if (Row.prefetch) Row.prefetch(row.dataset.indexUrl, Number(row.dataset.indexPages));
    });
  }

  open(card) {
    const title = card.querySelector('template[data-process-title]');
    const content = card.querySelector('template[data-process-content]');
    if (!content) return;

    this.heading.textContent = title ? title.content.textContent.trim() : '';
    this.body.replaceChildren(content.content.cloneNode(true));

    this.dialog.showModal();
    this.dialog.scrollTop = 0;
  }
}

if (!customElements.get('process-panel')) {
  customElements.define('process-panel', ProcessPanel);
}
